import type { PriceMessage } from '../../../../types/PricesMessage';
import type { ScheduleMessage } from '../../../../types/ScheduleMessage';
import { Box } from '../../../common/ui/Box';
import { FormikComparePortSelect } from '../FormikComparePortSelect';
import { PortPriceRow } from '../PortPriceRow';
import { RemoveCompareRowButton } from '../RemoveCompareRowButton';

import { ComparePriceButton } from './ComparePriceButton';
import { ComparePriceWrapper } from './ComparePriceWrapper';
import { useCompareSelectionState } from './hooks/useCompareSelectionState';

interface OverlayPortPriceInformationProps {
    index: number;
    isFetching: boolean;
    supplierData?: PriceMessage[];
    portId?: string;
    defaultContractId?: string;
    showRemoveIcon: boolean;
    onRemove: () => void;
    onSelectBoxClick: (portId: string | undefined, priceInfo: PriceMessage) => void;
    schedules: ScheduleMessage[];
}

export const OverlayPortPriceInformation = ({
    index,
    isFetching,
    supplierData,
    portId,
    defaultContractId,
    showRemoveIcon,
    onRemove,
    onSelectBoxClick,
    schedules,
}: OverlayPortPriceInformationProps) => {
    const { selection } = useCompareSelectionState();

    const isSelected = (priceInfo: PriceMessage) => {
        if (selection.portId !== portId) {
            return false;
        }

        return priceInfo.contractId
            ? selection.contractIds.includes(priceInfo.contractId)
            : selection.supplierIds.includes(priceInfo.supplierId);
    };

    return (
        <PortPriceRow>
            {showRemoveIcon ? <RemoveCompareRowButton onClick={onRemove} /> : null}
            <Box position="relative" width="180px" marginRight={16} style={{ flexShrink: 0 }}>
                <FormikComparePortSelect name={`portInformation[${index}].portId`} schedules={schedules} />
            </Box>

            <ComparePriceWrapper supplierData={supplierData} isFetching={isFetching}>
                {supplierData?.map((priceInfo) => {
                    const key = `${priceInfo.supplierId}:${priceInfo.contractId ?? ''}`;
                    return (
                        <Box key={key} width="240px">
                            <ComparePriceButton
                                selected={isSelected(priceInfo)}
                                price={priceInfo}
                                contractName={priceInfo.contract?.name}
                                isDefaultContract={priceInfo.contractId === defaultContractId}
                                onClick={() => onSelectBoxClick(portId, priceInfo)}
                            />
                        </Box>
                    );
                }) ?? []}
            </ComparePriceWrapper>
        </PortPriceRow>
    );
};
